import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { forwardRef } from 'react';
import { StyleSheet, TextInput, View, } from 'react-native';
import styled from 'styled-components/native';
import { match } from 'ts-pattern';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import { useKeyboardAwareFocus } from '@/components/KeyboardAwareFormScrollView';
import ClearIcon from '@/assets/icon/ic_clear.svg';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
/**
 * SignupBoxInput — 회원가입 화면 전용 박스형 입력 컴포넌트.
 * Figma 디자인: border 1.5px #EBEBEF rounded-8, height 50, font 18px Regular.
 * focus 시 파란 테두리(#0C76F7), error 시 빨간 테두리(#DB0B24).
 */
const SignupBoxInput = forwardRef(({ value, placeholder, label, isRequired, caption, captionColor, state, onChangeText, onFocus: onFocusProp, onBlur, onPress, onSubmitEditing, returnKeyType, keyboardType, maxLength, editable = true, isClearable = false, timer, rightElement, }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const localRef = React.useRef(null);
    const { notifyInputFocus } = useKeyboardAwareFocus();
    React.useImperativeHandle(ref, () => localRef.current);
    const isValid = state === undefined
        ? undefined
        : state === 'VALID' || state === 'PROGRESS';
    const handleFocus = () => {
        setIsFocused(true);
        onFocusProp === null || onFocusProp === void 0 ? void 0 : onFocusProp();
        // 래퍼(KeyboardAwareFormScrollView) 안이면 포커스된 input이 보이도록 스크롤 보정.
        notifyInputFocus();
    };
    const handleBlur = (e) => {
        setIsFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    };
    const handleClear = () => {
        var _a;
        onChangeText === null || onChangeText === void 0 ? void 0 : onChangeText('');
        (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const getBorderColor = () => {
        if (isValid === false)
            return color.red;
        if (isFocused)
            return color.brand40;
        return color.gray20;
    };
    const getCaptionColor = () => {
        if (captionColor)
            return captionColor;
        return match(isValid)
            .with(true, () => (isFocused ? color.brand40 : color.gray50v2))
            .with(false, () => color.red)
            .with(undefined, () => color.gray50v2)
            .exhaustive();
    };
    const renderInputContent = () => (_jsxs(_Fragment, { children: [_jsx(TextInput, { ref: localRef, value: value, onChangeText: onChangeText, placeholder: placeholder, placeholderTextColor: color.gray40, onFocus: handleFocus, onBlur: handleBlur, onSubmitEditing: onSubmitEditing, returnKeyType: returnKeyType, keyboardType: keyboardType, maxLength: maxLength, editable: editable && !onPress, style: [styles.input, !editable && styles.disabledInput] }), timer && _jsx(TimerText, { children: timer }), isClearable && value ? (_jsx(SccTouchableOpacity, { elementName: "signup_box_input_clear", hitSlop: 12, onPress: handleClear, children: _jsx(ClearIcon, { width: 20, height: 20 }) })) : null, rightElement] }));
    return (_jsxs(Container, { children: [label && (_jsxs(LabelRow, { children: [_jsx(LabelText, { children: label }), isRequired && _jsx(RequiredMark, { children: " *" })] })), _jsx(InputBox, { borderColor: getBorderColor(), children: onPress ? (_jsx(SccTouchableOpacity, { elementName: "signup_box_input_press", onPress: onPress, style: styles.inputRow, children: renderInputContent() })) : (_jsx(View, { style: styles.inputRow, children: renderInputContent() })) }), caption && (_jsx(CaptionText, { style: { color: getCaptionColor() }, children: caption }))] }));
});
SignupBoxInput.displayName = 'SignupBoxInput';
export default SignupBoxInput;
const styles = StyleSheet.create({
    input: {
        flex: 1,
        fontFamily: font.pretendardRegular,
        fontSize: 18,
        letterSpacing: -0.36,
        color: color.gray90v2,
        padding: 0,
        margin: 0,
        textAlignVertical: 'center',
        includeFontPadding: false,
    },
    disabledInput: {
        color: color.gray50v2,
    },
    inputRow: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
});
const Container = styled.View ``;
const LabelRow = styled.View `
  flex-direction: row;
  align-items: center;
  padding-left: 6px;
  margin-bottom: 8px;
`;
const LabelText = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: ${color.gray80v2};
`;
const RequiredMark = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 18px;
  color: ${color.red};
`;
const InputBox = styled.View `
  height: 50px;
  flex-direction: row;
  align-items: center;
  border-width: 1.5px;
  border-color: ${props => props.borderColor};
  border-radius: 8px;
  padding-horizontal: 12px;
  padding-vertical: 10px;
`;
const TimerText = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: ${color.red};
`;
const CaptionText = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 12px;
  line-height: 16px;
  margin-top: 4px;
`;
